import React, { createContext, useContext, useState, useEffect } from 'react';

const ServiceContext = createContext();

export const useServices = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
};

export const ServiceProvider = ({ children }) => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [location, setLocation] = useState('');

  // Mock data for services
  useEffect(() => {
    const mockServices = [
      {
        id: 1,
        name: "Elite Home Cleaning",
        category: "Cleaning",
        rating: 4.8,
        reviewCount: 127,
        price: "$$",
        location: "New York, NY",
        phone: "(555) 123-4567",
        email: "contact@elitehomecleaning.com",
        description: "Professional residential cleaning service with eco-friendly products. We provide thorough, reliable cleaning for homes and apartments.",
        services: ["Deep Cleaning", "Regular Maintenance", "Move-in/Move-out", "Post-Construction"],
        image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop",
        gallery: [
          "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=400&fit=crop",
          "https://images.unsplash.com/photo-1563453392212-326f5e854473?w=600&h=400&fit=crop"
        ],
        reviews: [
          { id: 1, author: "Sarah M.", rating: 5, comment: "Excellent service! Very thorough and professional.", date: "2024-01-15" },
          { id: 2, author: "Mike D.", rating: 4, comment: "Great cleaning, arrived on time.", date: "2024-01-10" }
        ]
      },
      {
        id: 2,
        name: "ProPlumb Solutions",
        category: "Plumbing",
        rating: 4.6,
        reviewCount: 89,
        price: "$$$",
        location: "New York, NY",
        phone: "(555) 234-5678",
        email: "info@proplumbsolutions.com",
        description: "Licensed and insured plumbing contractors specializing in residential and commercial plumbing repairs and installations.",
        services: ["Emergency Repairs", "Pipe Installation", "Drain Cleaning", "Water Heater Service"],
        image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=300&fit=crop",
        gallery: [
          "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&h=400&fit=crop"
        ],
        reviews: [
          { id: 1, author: "John K.", rating: 5, comment: "Fixed my emergency leak quickly and professionally.", date: "2024-01-12" }
        ]
      },
      {
        id: 3,
        name: "GreenThumb Landscaping",
        category: "Landscaping",
        rating: 4.9,
        reviewCount: 156,
        price: "$$",
        location: "Brooklyn, NY",
        phone: "(555) 345-6789",
        email: "hello@greenthumblandscaping.com",
        description: "Full-service landscaping company offering design, installation, and maintenance services for residential and commercial properties.",
        services: ["Landscape Design", "Lawn Care", "Tree Service", "Irrigation Systems"],
        image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop",
        gallery: [
          "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=400&fit=crop",
          "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop"
        ],
        reviews: [
          { id: 1, author: "Lisa R.", rating: 5, comment: "Transformed our backyard into a beautiful oasis!", date: "2024-01-08" }
        ]
      },
      {
        id: 4,
        name: "PowerUp Electrical",
        category: "Electrical",
        rating: 4.7,
        reviewCount: 73,
        price: "$$$",
        location: "Queens, NY",
        phone: "(555) 456-7890",
        email: "service@powerupelectrical.com",
        description: "Licensed electricians providing safe and reliable electrical services for homes and businesses.",
        services: ["Electrical Repairs", "Panel Upgrades", "Outlet Installation", "Lighting Design"],
        image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&h=300&fit=crop",
        gallery: [
          "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=600&h=400&fit=crop"
        ],
        reviews: [
          { id: 1, author: "Tom W.", rating: 5, comment: "Professional work, fair pricing. Highly recommended!", date: "2024-01-05" }
        ]
      }
    ];

    const mockCategories = [
      { id: 1, name: "Cleaning", icon: "FiHome", count: 45, image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=300&h=200&fit=crop" },
      { id: 2, name: "Plumbing", icon: "FiDroplet", count: 32, image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=300&h=200&fit=crop" },
      { id: 3, name: "Electrical", icon: "FiZap", count: 28, image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=300&h=200&fit=crop" },
      { id: 4, name: "Landscaping", icon: "FiSun", count: 56, image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=300&h=200&fit=crop" },
      { id: 5, name: "HVAC", icon: "FiWind", count: 23, image: "https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=300&h=200&fit=crop" },
      { id: 6, name: "Painting", icon: "FiEdit3", count: 41, image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=300&h=200&fit=crop" },
      { id: 7, name: "Roofing", icon: "FiTriangle", count: 19, image: "https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=300&h=200&fit=crop" },
      { id: 8, name: "Flooring", icon: "FiGrid", count: 34, image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=200&fit=crop" }
    ];

    setServices(mockServices);
    setCategories(mockCategories);
  }, []);

  const searchServices = (term, category, loc) => {
    setLoading(true);
    setSearchTerm(term);
    setSelectedCategory(category);
    setLocation(loc);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const getFilteredServices = () => {
    return services.filter(service => {
      const matchesTerm = !searchTerm || 
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.services.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = !selectedCategory || service.category === selectedCategory;
      const matchesLocation = !location || service.location.toLowerCase().includes(location.toLowerCase());
      
      return matchesTerm && matchesCategory && matchesLocation;
    });
  };

  const getServiceById = (id) => {
    return services.find(service => service.id === parseInt(id));
  };

  const value = {
    services,
    categories,
    loading,
    searchTerm,
    selectedCategory,
    location,
    searchServices,
    getFilteredServices,
    getServiceById,
    setSearchTerm,
    setSelectedCategory,
    setLocation
  };

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  );
};